import {
  expensesAdjustment,
  formatAmount,
  formatDateWithYear,
  isAutoload,
} from "../../../../utils/hooks/dataHandler";
import {
  boldAutoColumn,
  autoTextColor,
  beautyAutoLine,
  lineBreakText,
  redText,
  hasCrossYABasisHeader,
} from "../../../../utils/pdf/page";
import { BasePdfGenerator } from "../../../../utils/pdf/BasePdfGenerator";
import { sharedBasedPaymentLegends } from "../SharedBasedPayement/FurtherDeductionOutput";
import {
  buildAutoNote,
  buildAutoTable,
  enhanceReferenceColumn,
  formatReferenceDetails,
} from "../../../../utils/pdf/buildAutoTable";
import {
  getFifoShareUsage,
  getTrackerWorkingText,
} from "../SharedBasedPayement/sharedBasedPaymentCalculation";
import {
  arrangementOptions,
  costMethodOptions,
  formulaFieldsByArrangement,
  transactionTypeLabels,
} from "../SharedBasedPayement/sharedBasedPaymentData";

const footerSettings = false;

export const generateOtherIncomePdf = (
  forView,
  schedule,
  company,
  otherIncomeRows,
  expensesRows,
  totalOtherIncome,
  totalExpenses,
  netOtherIncome,
  foreignTaxSettings,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);

  let contentColumns = ["", "", "", "Sch"];
  let subTotalLines = [];
  let totalLines = [];
  let totalDoubleLines = [];

  if (forView) {
    contentColumns.push(redText("Reference"));
  }

  let contentData = [];
  let labelRow = ["", "$", "$", ""];
  if (forView) labelRow.push("");
  contentData.push(labelRow);

  const haveIncomeRows = otherIncomeRows && otherIncomeRows.length > 0;
  const haveManyIncomeRows = haveIncomeRows && otherIncomeRows.length > 1;
  const haveExpensesRows = expensesRows && expensesRows.length > 0;
  const haveManyExpensesRows = haveExpensesRows && expensesRows.length > 1;

  const addBackFn = () => {
    if (schedule.taxAdjustment === "Added Back")
      contentData.push([
        "",
        "",
        autoTextColor("Added back / Main Comp", [0, 0, 0]),
      ]);

    if (schedule.taxAdjustment === "Unremitted Foreign Income") {
      contentData.push([
        "",
        "",
        autoTextColor(foreignTaxSettings?.unremittedSchNo || "", [0, 0, 0]),
      ]);
    }

    if (
      foreignTaxSettings.foreignTaxSuffered &&
      foreignTaxSettings.foreignTaxSuffered > 0 &&
      (schedule.taxAdjustment === "Unremitted Foreign Income" ||
        schedule.taxAdjustment === "Added Back")
    ) {
      contentData.push([]);

      contentData.push([
        boldAutoColumn("Foreign Tax Suffered"),
        "",
        formatAmount(foreignTaxSettings.foreignTaxSuffered, false),
      ]);
      totalLines.push(contentData.length - 1);
      totalDoubleLines.push(contentData.length - 1);
      if (foreignTaxSettings.isTagToFTC) {
        contentData.push([
          "",
          "",
          lineBreakText(foreignTaxSettings.ftcSchNo || "", {
            top: 3,
            bottom: 0,
          }),
        ]);
      }
    }
  };

  if (haveIncomeRows) {
    contentData.push([boldAutoColumn("Other Income")]);

    otherIncomeRows.map((row) => {
      let content = [row.description];
      if (!haveManyIncomeRows) {
        content.push("", formatAmount(row.amount));
      } else content.push(formatAmount(row.amount), "");
      content.push(row.source?.sch_no ?? "");

      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }

      contentData.push(content);
    });

    if (!haveManyIncomeRows && !haveExpensesRows) {
      totalLines.push(contentData.length - 1);
      totalDoubleLines.push(contentData.length - 1);
      addBackFn();
    } else {
      if (haveManyIncomeRows) {
        contentData.push([
          !haveExpensesRows ? boldAutoColumn("Net Other Income") : "",
          "",
          formatAmount(totalOtherIncome),
        ]);
        if (!haveExpensesRows) totalLines.push(contentData.length - 2);
        else subTotalLines.push(contentData.length - 2);
      }

      if (!haveExpensesRows) {
        totalLines.push(contentData.length - 1);
        totalDoubleLines.push(contentData.length - 1);
        addBackFn();
      }
    }
  }

  if (haveExpensesRows) {
    contentData.push([boldAutoColumn("Less: Direct expenses")]);

    expensesRows.map((row) => {
      let content = [row.description];
      if (!haveManyExpensesRows) {
        content.push("", formatAmount(-row.amount));
      } else content.push(formatAmount(-row.amount), "");
      content.push(row.source?.sch_no ?? "");

      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }

      contentData.push(content);
    });

    if (!haveIncomeRows && !haveManyExpensesRows) {
      totalLines.push(contentData.length - 2);
      totalLines.push(contentData.length - 1);
      totalDoubleLines.push(contentData.length - 1);

      addBackFn();
    } else {
      if (haveManyExpensesRows) {
        contentData.push([
          !haveIncomeRows ? boldAutoColumn("Net Other Income") : "",
          "",
          formatAmount(-totalExpenses),
          "",
        ]);
        if (!haveIncomeRows) totalLines.push(contentData.length - 2);
        else subTotalLines.push(contentData.length - 2);
      }
      if (!haveIncomeRows) {
        totalLines.push(contentData.length - 1);
        totalDoubleLines.push(contentData.length - 1);
        addBackFn();
      }
    }

    if (haveIncomeRows && haveExpensesRows) {
      contentData.push([
        boldAutoColumn("Net Other Income"),
        "",
        formatAmount(netOtherIncome),
      ]);
      totalLines.push(contentData.length - 2);
      totalLines.push(contentData.length - 1);
      totalDoubleLines.push(contentData.length - 1);
      addBackFn();
    }
  }

  if (contentData.length > 0) {
    const labelRows = [1, 2];
    const totalRows = [2];
    const subTotalRows = [1];

    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left", minCellWidth: 50 },
        1: { halign: "right", cellWidth: 25 },
        2: { halign: "right", cellWidth: 25 },
        3: { halign: "center" },
        4: { halign: "center" },
      },
      didParseCell: (data) => {
        if (data.row.index === 0 && [1, 2].includes(data.column.index)) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: (data) => {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 && labelRows.includes(data.column.index)) ||
            (totalLines.includes(data.row.index) &&
              totalRows.includes(data.column.index)) ||
            (subTotalLines.includes(data.row.index) &&
              subTotalRows.includes(data.column.index)),
          totalDoubleLines.includes(data.row.index) &&
            totalRows.includes(data.column.index),
          24
        );
      },
      lineSettings: {
        totalLines,
        subTotalLines,
        totalDoubleLines,
        labelRows,
        totalRows,
        subTotalRows,
      },
    });
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    (otherIncomeRows || []).concat(expensesRows || []),
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateInvestIncomePdf = (
  forView,
  schedule,
  company,
  investIncomeRows,
  expensesRows,
  totalInvestIncome,
  totalExpenses,
  netIncome,
  foreignTaxSettings,
  shareOfExpenses,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);

  const addBackFn = () => {
    if (schedule.taxAdjustment === "Added Back")
      contentData.push([
        "",
        "",
        autoTextColor("Added back / Main Comp", [0, 0, 0]),
      ]);

    if (schedule.taxAdjustment === "Unremitted Foreign Income") {
      contentData.push([
        "",
        "",
        autoTextColor(foreignTaxSettings?.unremittedSchNo || "", [0, 0, 0]),
      ]);
    }

    if (
      foreignTaxSettings.foreignTaxSuffered &&
      foreignTaxSettings.foreignTaxSuffered > 0 &&
      (schedule.taxAdjustment === "Unremitted Foreign Income" ||
        schedule.taxAdjustment === "Added Back")
    ) {
      contentData.push([]);

      contentData.push([
        boldAutoColumn("Foreign Tax Suffered"),
        "",
        formatAmount(foreignTaxSettings.foreignTaxSuffered, false),
      ]);
      totalLines.push(contentData.length - 1);
      totalDoubleLines.push(contentData.length - 1);
      if (foreignTaxSettings.isTagToFTC) {
        contentData.push([
          "",
          "",
          lineBreakText(foreignTaxSettings.ftcSchNo || "", {
            top: 3,
            bottom: 0,
          }),
        ]);
      }
    }
  };

  let contentColumns = ["", "", "", "Sch"];
  let subTotalLines = [];
  let totalLines = [];
  let totalDoubleLines = [];

  if (forView) {
    contentColumns.push(redText("Reference"));
  }

  let contentData = [];
  let labelRow = ["", "$", "$", ""];
  if (forView) labelRow.push("");
  contentData.push(labelRow);

  if (investIncomeRows && investIncomeRows.length > 0) {
    contentData.push([]);

    const onlyOneRow = investIncomeRows.length === 1;
    investIncomeRows.map((row) => {
      let content = [row.description];
      if (onlyOneRow) {
        content.push("", formatAmount(row.amount));
      } else content.push(formatAmount(row.amount), "");
      content.push(row.source?.sch_no ?? "");

      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }
      contentData.push(content);
    });

    if (
      !expensesRows ||
      (expensesRows.length === 0 &&
        (!shareOfExpenses || shareOfExpenses.amount === 0))
    ) {
      totalLines.push(contentData.length - 1);
      totalDoubleLines.push(contentData.length - 1);
      addBackFn();
    } else {
      if (!onlyOneRow) {
        subTotalLines.push(contentData.length - 1);
        contentData.push(["", "", formatAmount(totalInvestIncome)]);
      }

      contentData.push([]);
    }
  }

  if (expensesRows && expensesRows.length > 0) {
    contentData.push([boldAutoColumn("Less: Direct expenses")]);

    const onlyOneRow = expensesRows.length === 1;
    expensesRows.map((row) => {
      let content = [row.description];
      if (onlyOneRow) {
        content.push("", formatAmount(-row.amount));
      } else content.push(formatAmount(-row.amount), "");
      content.push(row.source?.sch_no ?? "");

      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }
      contentData.push(content);
    });

    if (
      !investIncomeRows ||
      (investIncomeRows.length === 0 &&
        (!shareOfExpenses || shareOfExpenses.amount === 0))
    ) {
      totalLines.push(contentData.length - 1);
      totalDoubleLines.push(contentData.length - 1);
      addBackFn();
    } else {
      if (!onlyOneRow) {
        subTotalLines.push(contentData.length - 1);
        contentData.push(["", "", formatAmount(-totalExpenses)]);
      }
    }
  }

  if (shareOfExpenses) {
    contentData.push([]);
    contentData.push([
      boldAutoColumn("Less: Share of expenses"),
      "",
      formatAmount(-shareOfExpenses.amount),
      shareOfExpenses.source ?? "",
    ]);

    contentData.push([]);
    contentData.push([
      boldAutoColumn("Net Income"),
      "",
      formatAmount(netIncome),
    ]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
    addBackFn();
  }

  if (contentData.length > 0)
    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left", minCellWidth: 50 },
        1: { halign: "right", cellWidth: 25 },
        2: { halign: "right", cellWidth: 25 },
        3: { halign: "center" },
        4: { halign: "center" },
      },
      didParseCell: (data) => {
        if (data.row.index === 0 && [1, 2].includes(data.column.index)) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: (data) => {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 && [1, 2].includes(data.column.index)) ||
            (totalLines.includes(data.row.index) &&
              [2].includes(data.column.index)) ||
            (subTotalLines.includes(data.row.index) &&
              [1].includes(data.column.index)),
          totalDoubleLines.includes(data.row.index) &&
            [2].includes(data.column.index),
          24
        );
      },
      lineSettings: {
        totalLines,
        totalDoubleLines,
        labelRows: [1, 2],
        totalRows: [2],
        subTotalLines,
        subTotalRows: [1],
      },
    });

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    (investIncomeRows || []).concat(expensesRows || []),
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateServiceIncomePdf = (
  forView,
  schedule,
  company,
  otherIncomeRows,
  expensesRows,
  totalInvestIncome,
  totalExpenses,
  netIncome,
  shareOfExpenses,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);

  const addBackFn = () => {
    if (schedule.taxAdjustment === "Added Back")
      contentData.push([
        "",
        "",
        autoTextColor("Added back / Main Comp", [0, 0, 0]),
      ]);
  };

  let contentColumns = ["", "", "", "Sch"];
  let subTotalLines = [];
  let totalLines = [];
  let totalDoubleLines = [];

  if (forView) {
    contentColumns.push(redText("Reference"));
  }

  let contentData = [];
  let labelRow = ["", "$", "$", ""];
  if (forView) labelRow.push("");
  contentData.push(labelRow);

  if (otherIncomeRows && otherIncomeRows.length > 0) {
    contentData.push([]);

    const onlyOneRow = otherIncomeRows.length === 1;
    otherIncomeRows.map((row) => {
      let content = [row.description];
      if (onlyOneRow) {
        content.push("", formatAmount(row.amount));
      } else content.push(formatAmount(row.amount), "");
      content.push(row.source?.sch_no ?? "");

      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }
      contentData.push(content);
    });

    if (
      !expensesRows ||
      (expensesRows.length === 0 &&
        (!shareOfExpenses || shareOfExpenses.amount === 0))
    ) {
      totalLines.push(contentData.length - 1);
      totalDoubleLines.push(contentData.length - 1);
      addBackFn();
    } else {
      if (!onlyOneRow) {
        subTotalLines.push(contentData.length - 1);
        contentData.push(["", "", formatAmount(totalInvestIncome)]);
      }

      contentData.push([]);
    }
  }

  if (expensesRows && expensesRows.length > 0) {
    contentData.push([boldAutoColumn("Less: Service expenses")]);

    const onlyOneRow = expensesRows.length === 1;
    expensesRows.map((row) => {
      let content = [row.description];
      if (onlyOneRow) {
        content.push("", formatAmount(-row.amount));
      } else content.push(formatAmount(-row.amount), "");
      content.push(row.source?.sch_no ?? "");

      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }
      contentData.push(content);
    });

    if (
      !otherIncomeRows ||
      (otherIncomeRows.length === 0 &&
        (!shareOfExpenses || shareOfExpenses.amount === 0))
    ) {
      totalLines.push(contentData.length - 1);
      totalDoubleLines.push(contentData.length - 1);
      addBackFn();
    } else {
      if (!onlyOneRow) {
        subTotalLines.push(contentData.length - 1);
        contentData.push(["", "", formatAmount(-totalExpenses)]);
      }
    }
  }

  if (shareOfExpenses) {
    contentData.push([]);
    contentData.push([
      boldAutoColumn("Less: Share of expenses"),
      "",
      formatAmount(-shareOfExpenses.amount),
      shareOfExpenses.source ?? "",
    ]);

    contentData.push([]);
    contentData.push([
      boldAutoColumn("Net Service Income"),
      "",
      formatAmount(netIncome),
    ]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
    addBackFn();
  }

  if (contentData.length > 0)
    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left", minCellWidth: 50 },
        1: { halign: "right", cellWidth: 25 },
        2: { halign: "right", cellWidth: 25 },
        3: { halign: "center" },
        4: { halign: "center" },
      },
      didParseCell: (data) => {
        if (data.row.index === 0 && [1, 2].includes(data.column.index)) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: (data) => {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 && [1, 2].includes(data.column.index)) ||
            (totalLines.includes(data.row.index) &&
              [2].includes(data.column.index)) ||
            (subTotalLines.includes(data.row.index) &&
              [1].includes(data.column.index)),
          totalDoubleLines.includes(data.row.index) &&
            [2].includes(data.column.index),
          24
        );
      },
      lineSettings: {
        totalLines,
        totalDoubleLines,
        labelRows: [1, 2],
        totalRows: [2],
        subTotalLines,
        subTotalRows: [1],
      },
    });

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    (otherIncomeRows || []).concat(expensesRows || []),
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateDonationPdf = (
  forView,
  schedule,
  company,
  donationRows,
  totalDonation,
  additionDeduction,
  additionDeductionAmount,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);
  let contentColumns = ["", "", "", "Sch"];
  let totalLines = [];
  let totalDoubleLines = [];

  if (forView) {
    contentColumns.push(redText("Reference"));
  }

  let contentData = [];
  let labelRow = ["", "", "$", ""];

  if (donationRows && donationRows.length > 0) {
    if (forView) labelRow.push("");
    contentData.push(labelRow);

    contentData.push([
      boldAutoColumn("Approved IPCs"),
      boldAutoColumn("Details"),
    ]);
    donationRows.map((row) => {
      let content = [row.approvedIPC, row.details, formatAmount(row.amount)];
      content.push(row.source ?? "");

      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }
      contentData.push(content);
    });
    contentData.push(["", "", formatAmount(totalDonation)]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);

    contentData.push([]);
    contentData.push([
      boldAutoColumn("Addition Deductions (%)"),
      additionDeduction + "%",
      formatAmount(additionDeductionAmount),
    ]);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);

    contentData.push(["", "", autoTextColor("Deduct / Main Comp", [0, 0, 0])]);
  }

  if (contentData.length > 0) {
    const totalRows = [2];

    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left", cellWidth: 65 },
        1: { halign: "left", cellWidth: 50 },
        2: { halign: "right", cellWidth: 28 },
        3: { halign: "center", minCellWidth: 15 },
        4: { halign: "center" },
      },
      didParseCell: (data) => {
        if (data.row.index === 0 && [2].includes(data.column.index)) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: (data) => {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 && totalRows.includes(data.column.index)) ||
            (totalLines.includes(data.row.index) &&
              totalRows.includes(data.column.index)),
          totalDoubleLines.includes(data.row.index) &&
            totalRows.includes(data.column.index),
          26
        );
      },
      lineSettings: {
        totalLines,
        totalDoubleLines,
        labelRows: totalRows,
        totalRows,
      },
    });
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    donationRows,
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateFurtherDeductionPdf = (
  forView,
  schedule,
  company,
  expensesRows,
  totalExpenses,
  isExpenseCapped,
  deductionRate,
  expenseCappedAmount,
  finalDeduction,
  notes,
  reviewNotes,
  isExcel = false,
  {
    exchangeRate = 1,
    excessDeductionRate = 0,
    isExcessDeductionEnabled = Number(excessDeductionRate) > 0,
    excessDeductionDescription = "",
    excessDeduction = 0,
    totalDeduction = Number(finalDeduction || 0) + Number(excessDeduction || 0),
  } = {}
) => {
  const pdf = new BasePdfGenerator(false, isExcel);
  const formatDeductionAmount = (amount) => {
    const formatted = formatAmount(amount ?? 0, false);
    return formatted === "0" ? "0.00" : formatted;
  };

  let contentColumns = ["", "", "Sch"];
  let totalLines = [];
  let totalDoubleLines = [];

  if (forView) {
    contentColumns.push(redText("Reference"));
  }

  let contentData = [];
  let labelRow = [boldAutoColumn("Qualifying Expenses"), "$", ""];
  if (forView) labelRow.push("");
  contentData.push(labelRow);

  if (expensesRows && expensesRows.length > 0) {
    expensesRows.map((row) => {
      let content = [
        row.description,
        formatAmount(row.amount),
        row.source?.sch_no ?? "",
      ];

      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }
      contentData.push(content);
    });

    contentData.push(["", formatAmount(totalExpenses)]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
  }

  contentData.push([]);

  contentData.push([boldAutoColumn("Further deduction computation", true)]);
  contentData.push(["Further deduction rate", `${deductionRate}%`]);

  if (isExpenseCapped) {
    contentData.push([
      "Expense cap (SGD)",
      formatDeductionAmount(expenseCappedAmount),
    ]);
    if (company.currency && company.currency !== "SGD") {
      contentData.push([
        lineBreakText("Exchange rate (SGD per functional currency unit)"),
        String(exchangeRate),
      ]);
    }
  }
  if (isExpenseCapped && isExcessDeductionEnabled) {
    contentData.push([
      "Further deduction rate above cap",
      `${excessDeductionRate || 0}%`,
    ]);
  }

  contentData.push([]);

  contentData.push([
    boldAutoColumn(
      isExpenseCapped ? "Further deduction within cap" : "Further deduction",
      true
    ),
    formatDeductionAmount(finalDeduction),
  ]);

  totalLines.push(contentData.length - 2);
  totalLines.push(contentData.length - 1);
  totalDoubleLines.push(contentData.length - 1);

  contentData.push(["", autoTextColor("Deduct / Main Comp", [0, 0, 0])]);

  if (isExpenseCapped && isExcessDeductionEnabled) {
    contentData.push([]);
    contentData.push([
      boldAutoColumn(
        excessDeductionDescription?.trim() || "Further deduction on excess",
        true
      ),
      formatDeductionAmount(excessDeduction),
    ]);
    totalLines.push(contentData.length - 2, contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);

    if (Number(excessDeductionRate) > 0 && Number(excessDeduction) > 0) {
      contentData.push(["", autoTextColor("Deduct / Main Comp", [0, 0, 0])]);
    }

    contentData.push([]);
    contentData.push([
      boldAutoColumn("Total further deduction", true),
      formatDeductionAmount(totalDeduction),
    ]);
    totalLines.push(contentData.length - 2, contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
  }

  if (contentData.length > 0) {
    const totalRows = [1];

    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left", minCellWidth: 80 },
        1: { halign: "right", cellWidth: 25 },
        2: { halign: "center", minCellWidth: 15 },
        3: { halign: "center" },
      },
      didParseCell: (data) => {
        if (data.row.index === 0 && [1].includes(data.column.index)) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: (data) => {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 && totalRows.includes(data.column.index)) ||
            (totalLines.includes(data.row.index) &&
              totalRows.includes(data.column.index)),
          totalDoubleLines.includes(data.row.index) &&
            totalRows.includes(data.column.index),
          24
        );
      },
      lineSettings: {
        totalLines,
        totalDoubleLines,
        totalRows,
        labelRows: totalRows,
      },
    });
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    expensesRows,
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateSharedBasedPaymentPdf = (
  forView,
  schedule,
  company,
  data = {},
  calculationResults = {},
  notes,
  reviewNotes,
  isExcel = false,
  showFifoShares = data.showFifoShares
) => {
  const pdf = new BasePdfGenerator(true, isExcel);
  const costPools = Array.isArray(data.costPools) ? data.costPools : [];
  const transactions = Array.isArray(data.transactions)
    ? data.transactions
    : [];
  const claimLines = Array.isArray(data.claimLines) ? data.claimLines : [];
  const trackerResultsByPoolId = Object.fromEntries(
    (calculationResults.trackerResults || []).map(({ pool, summary }) => [
      pool.poolId,
      summary,
    ])
  );
  const claimLinesById = Object.fromEntries(
    claimLines.map((line) => [line.claimLineId, line])
  );
  const nextTableStartY = () => {
    if (isExcel) return undefined;

    const pageStartY = hasCrossYABasisHeader(company) ? 65 : 60;
    // Preserve a blank line above section titles, including on a new page.
    if (pdf.startY > 160) {
      pdf.doc.addPage("a4", "landscape");
      pdf.startY = pageStartY;
    }
    return Math.max(pdf.startY || 0, pageStartY) + pdf.lineHeight;
  };
  const tableLayout = isExcel
    ? { lineSettings: {} }
    : {
        tableWidth: "auto",
        margin: {
          top: pdf.initY + (hasCrossYABasisHeader(company) ? 45 : 40),
          bottom: 20.61,
          left: pdf.leftX - 1,
          right: pdf.leftX - 1,
        },
        styles: {
          fontSize: 9,
          cellPadding: 1.5,
          valign: "middle",
          overflow: "linebreak",
        },
        rowPageBreak: "avoid",
        didParseCell: (cellData) => {
          // Align each heading with its values, leaving spanning labels intact.
          if (cellData.section === "head" && cellData.cell.colSpan === 1) {
            const columnStyle =
              cellData.table.styles.columnStyles[cellData.column.index] || {};
            cellData.cell.styles.halign = columnStyle.halign || "left";
            if (columnStyle.minCellWidth) {
              cellData.cell.styles.minCellWidth = columnStyle.minCellWidth;
            }
          }
        },
      };

  if (data.useCostTracker) {
    costPools.forEach((pool) => {
      const trackerResult = trackerResultsByPoolId[pool.poolId] || {};
      buildAutoTable({
        ...tableLayout,
        styles: {
          fontSize: 10,
        },
        pdf,
        head: [
          [
            "Pool",
            "Issuer",
            "Share class",
            "Cost method",
            "Closing Shares",
            "Closing cost",
          ],
        ],
        body: [
          [
            pool.poolName || pool.poolId || "",
            pool.issuer || "",
            pool.shareClass || "",
            costMethodOptions.find((option) => option.value === pool.method)
              ?.label ||
              pool.method ||
              "",
            trackerResult.issue
              ? "Review required"
              : formatAmount(trackerResult.closingShares || 0),
            trackerResult.issue
              ? "Review required"
              : formatAmount(trackerResult.closingCost || 0),
          ],
        ],
        schedule,
        company,
        addPlainTitle: true,
        tableTitle: `Acquired Shares Cost Tracker`,
        tableTitleFontSize: 12,
        customStartY: nextTableStartY(),
        customHeadStyles: {
          fontStyle: "bold",
          textColor: [0, 0, 0],
        },
        columnStyles: {
          0: { halign: "left", cellWidth: "auto", minCellWidth: 24 },
          1: { halign: "center", cellWidth: "auto", minCellWidth: 36 },
          2: { halign: "center", cellWidth: 24 },
          3: { halign: "center", cellWidth: "auto", minCellWidth: 30 },
          4: { halign: "right", cellWidth: 32 },
          5: { halign: "right", cellWidth: 30 },
        },
      });

      const poolTransactions = transactions
        .filter((row) => row.poolId === pool.poolId)
        .sort(
          (left, right) =>
            String(left.date || "").localeCompare(String(right.date || "")) ||
            Number(left.sequence || 0) - Number(right.sequence || 0)
        );
      const fifoShareUsage =
        showFifoShares && pool.method === "fifo"
          ? getFifoShareUsage(poolTransactions, trackerResult)
          : null;
      const transactionHead = [
        "Txn no.",
        "Date",
        "Type",
        "Shares",
        "Share cost",
        "Charges",
        "Linked line",
        "Column I",
        "Method working",
      ];
      if (forView) transactionHead.push(redText("Reference"));

      const transactionBody = poolTransactions.map((row) => {
        const working = trackerResult.transactionWorkings?.[row.transactionId];
        const linkedLine = claimLinesById[row.linkedClaimLineId];
        const output = [
          row.serialNo || row.transactionId || "",
          formatDateWithYear(row.date, "short"),
          [
            transactionTypeLabels[row.type] || row.type || "",
            row.type === "otherOutflow" ? row.outflowDescription?.trim() : "",
          ]
            .filter(Boolean)
            .join(" - "),
          formatAmount(row.shares || 0),
          row.type === "acquisition" ? formatAmount(row.shareCost || 0) : "-",
          row.type === "acquisition"
            ? formatAmount(row.directAcquisitionCharges || 0)
            : "-",
          linkedLine?.lineId || "",
          row.type === "eebrTransfer" && working?.releasedCost != null
            ? formatAmount(working.releasedCost)
            : "-",
          trackerResult.issue ||
            getTrackerWorkingText(working, pool.method, transactions),
        ];
        if (forView) output.push(redText(row.reference || ""));
        return output;
      });

      buildAutoTable({
        ...tableLayout,
        pdf,
        head: [transactionHead],
        body: transactionBody.length
          ? transactionBody
          : [
              [
                {
                  content: "No transactions",
                  colSpan: transactionHead.length,
                  styles: { halign: "center", textColor: [100, 100, 100] },
                },
              ],
            ],
        schedule,
        company,
        // The pool summary and its transactions share the same width and
        // header treatment, with only a small internal gap between them.
        customStartY: isExcel
          ? undefined
          : Math.max(pdf.doc.lastAutoTable.finalY + 3, 60),
        customHeadStyles: {
          fontStyle: "bold",
          textColor: [0, 0, 0],
        },
        columnStyles: {
          0: { halign: "left", cellWidth: 22 },
          1: { halign: "center", cellWidth: 24 },
          2: { halign: "center", cellWidth: "auto", minCellWidth: 26 },
          3: { halign: "right", cellWidth: 18 },
          4: { halign: "right", cellWidth: 23 },
          5: { halign: "right", cellWidth: 21 },
          6: { halign: "center", cellWidth: 20 },
          7: { halign: "right", cellWidth: 22 },
          8: { halign: "center", cellWidth: "auto", minCellWidth: 58 },
          ...(forView
            ? { 9: { halign: "center", cellWidth: "auto", minCellWidth: 26 } }
            : {}),
        },
      });

      if (fifoShareUsage) {
        const fifoOutflows = fifoShareUsage.outflows;
        const fifoHead = [
          "Txn no.",
          "Date",
          "Type",
          "Shares",
          ...fifoOutflows.map((outflow, index) => {
            const reference = outflow.serialNo || outflow.transactionId;
            const date = formatDateWithYear(outflow.date, "short");
            const title = "";
            const separator = isExcel ? ": " : "\n";
            return `${title ? title + separator : ""}${date || "No date"} · ${reference}`;
          }),
          "No. of shares c/f",
          "Share cost",
          "Charges",
        ];
        const fifoBody = poolTransactions.map((row) => [
          row.serialNo || row.transactionId || "",
          formatDateWithYear(row.date, "short"),
          [
            transactionTypeLabels[row.type] || row.type || "",
            row.type === "otherOutflow" ? row.outflowDescription?.trim() : "",
          ]
            .filter(Boolean)
            .join(" - "),
          formatAmount(row.shares || 0),
          ...fifoOutflows.map((outflow) => {
            const used =
              fifoShareUsage.usedBySourceAndOutflow[row.transactionId]?.[
                outflow.transactionId
              ] || 0;
            if (row.transactionId === outflow.transactionId) {
              const total = fifoShareUsage.usedByOutflow[outflow.transactionId];
              return total ? formatAmount(total) : "-";
            }
            return row.type === "acquisition" && used
              ? `(${formatAmount(used)})`
              : "-";
          }),
          row.type === "acquisition" &&
          fifoShareUsage.closingSharesBySource[row.transactionId] > 0
            ? formatAmount(
                fifoShareUsage.closingSharesBySource[row.transactionId]
              )
            : "-",
          row.type === "acquisition" ? formatAmount(row.shareCost || 0) : "-",
          row.type === "acquisition"
            ? formatAmount(row.directAcquisitionCharges || 0)
            : "-",
        ]);
        const closingColumnIndex = 4 + fifoOutflows.length;

        buildAutoTable({
          ...tableLayout,
          pdf,
          head: [fifoHead],
          body: fifoBody.length
            ? fifoBody
            : [
                [
                  {
                    content: "No transactions",
                    colSpan: fifoHead.length,
                    styles: { halign: "center", textColor: [100, 100, 100] },
                  },
                ],
              ],
          schedule,
          company,
          addPlainTitle: true,
          tableTitle: "FIFO calculations",
          tableTitleFontSize: 12,
          customStartY: nextTableStartY(),
          customHeadStyles: {
            fontStyle: "bold",
            textColor: [0, 0, 0],
          },
          horizontalPageBreak: true,
          horizontalPageBreakRepeat: [0, 1, 2, 3],
          columnStyles: {
            0: { halign: "left", cellWidth: 22 },
            1: { halign: "center", cellWidth: 24 },
            2: { halign: "center", cellWidth: "auto", minCellWidth: 26 },
            3: { halign: "right", cellWidth: 18 },
            ...Object.fromEntries(
              fifoOutflows.map((_, index) => [
                4 + index,
                { halign: "right", cellWidth: 22 },
              ])
            ),
            [closingColumnIndex]: { halign: "right", cellWidth: 25 },
            [closingColumnIndex + 1]: { halign: "right", cellWidth: 23 },
            [closingColumnIndex + 2]: { halign: "right", cellWidth: 21 },
          },
        });
      }
    });
  }

  const deductionHead = [
    "Line ID",
    "Arrangement",
    "Description",
    "Exercise / vesting",
    "Recharge / liability",
    "I",
    "II",
    "III",
    "IV",
    "Employee payment",
    "Status",
    "Deduction",
  ];
  const deductionBody = claimLines.map((line) => {
    const lineResult =
      calculationResults.lineResultsById?.[line.claimLineId] || {};
    const applicableFields = formulaFieldsByArrangement[line.arrangement] || [];
    const amount = (field) => {
      if (!applicableFields.includes(field)) return "-";
      const value =
        field === "amountI" && lineResult.trackerLinked
          ? lineResult.columnIAmount
          : line[field];
      if (value === "" || value === null || value === undefined) return "";
      return formatAmount(value);
    };

    return [
      line.lineId || "",
      arrangementOptions.find((option) => option.value === line.arrangement)
        ?.label ||
        line.arrangement ||
        "",
      line.description || "",
      formatDateWithYear(line.exerciseOrVestingDate, "short"),
      line.arrangement === "companyTreasury"
        ? ""
        : formatDateWithYear(line.rechargeOrLiabilityDate, "short"),
      amount("amountI"),
      amount("amountII"),
      amount("amountIII"),
      amount("amountIV"),
      line.employeePayment === "" || line.employeePayment == null
        ? ""
        : formatAmount(line.employeePayment),
      lineResult.status?.label || "Pending",
      formatAmount(lineResult.roundedDeduction || 0),
    ];
  });
  const totalRowIndex = deductionBody.length;
  deductionBody.push([
    {
      content: "Total qualifying deduction",
      colSpan: 11,
      styles: { fontStyle: "bold", halign: "right" },
    },
    formatAmount(calculationResults.output?.qualifyingDeductionSgd || 0),
  ]);
  deductionBody.push([
    { content: "", colSpan: 11 },
    autoTextColor("Deduct / Main Comp", [0, 0, 0]),
  ]);

  buildAutoTable({
    pdf,
    head: [deductionHead],
    ...tableLayout,
    body: deductionBody,
    schedule,
    company,
    addPlainTitle: true,
    tableTitle: "Qualifying Deduction Schedule",
    tableTitleFontSize: 12,
    customStartY: nextTableStartY(),
    columnStyles: {
      0: { halign: "center", cellWidth: 14 },
      1: { halign: "left", cellWidth: "auto", minCellWidth: 30 },
      2: { halign: "left", cellWidth: "auto", minCellWidth: 32 },
      3: { halign: "center", cellWidth: 24 },
      4: { halign: "center", cellWidth: 24 },
      5: { halign: "right", cellWidth: 17 },
      6: { halign: "right", cellWidth: 17 },
      7: { halign: "right", cellWidth: 17 },
      8: { halign: "right", cellWidth: 17 },
      9: { halign: "right", cellWidth: 21 },
      10: { halign: "center", cellWidth: 18 },
      11: { halign: "right", cellWidth: 26 },
    },
    didDrawCell: (cellData) => {
      const isTotalAmount =
        cellData.section === "body" &&
        cellData.row.index === totalRowIndex &&
        cellData.column.index === 11;
      if (isTotalAmount) {
        // Draw on the total cell itself so its overline survives page breaks.
        pdf.doc.setDrawColor(0);
        pdf.doc.setLineWidth(0.1);
        pdf.doc.line(
          cellData.cell.x + 1.5,
          cellData.cell.y,
          cellData.cell.x + cellData.cell.width - 1.5,
          cellData.cell.y
        );
      }
      beautyAutoLine(
        cellData,
        pdf.doc,
        isTotalAmount,
        isTotalAmount,
        cellData.cell.width - 3
      );
    },
    lineSettings: {
      totalLines: [totalRowIndex],
      totalDoubleLines: [totalRowIndex],
      totalRows: [11],
      labelRows: [11],
    },
  });

  buildAutoTable({
    ...tableLayout,
    styles: {
      fontSize: 10,
    },
    pdf,
    head: [["Column", "Meaning", "Description"]],
    body: sharedBasedPaymentLegends.map(({ label, title, description }) => [
      label,
      title,
      description,
    ]),
    schedule,
    company,
    addPlainTitle: true,
    tableTitle: "I-IV legend",
    tableTitleFontSize: 12,
    customStartY: nextTableStartY(),
    columnStyles: {
      0: { halign: "center", cellWidth: 18 },
      1: { halign: "left", cellWidth: 55 },
      2: { halign: "left", cellWidth: "auto" },
    },
  });

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    transactions.concat(claimLines),
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateInterestExpensePdf = (
  forView,
  schedule,
  company,
  interestExpenseRows,
  assetsRows,
  totalInterestExpenses,
  totalAssets,
  totalNonIncomeAssets,
  disAllowableInterest,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);

  let contentColumns = ["", "", "Sch"];
  let totalLines = [];
  let totalDoubleLines = [];

  if (forView) {
    contentColumns.push(redText("Reference"));
  }

  let contentData = [];
  let labelRow = [boldAutoColumn("Interest Expense"), "$", ""];
  if (forView) labelRow.push("");
  contentData.push(labelRow);
  contentData.push([]);

  if (interestExpenseRows && interestExpenseRows.length > 0) {
    interestExpenseRows.map((row) => {
      let content = [
        row.description,
        formatAmount(row.amount || 0),
        row.source?.sch_no ?? "",
      ];

      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }

      contentData.push(content);
    });
    contentData.push([
      boldAutoColumn("Total Interest Expense"),
      formatAmount(totalInterestExpenses || 0),
    ]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
  }

  if (contentData.length > 0) {
    const totalRows = [1];
    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left", minCellWidth: 60 },
        1: { halign: "right", cellWidth: 25 },
        2: { halign: "center", minCellWidth: 15 },
        3: { halign: "center" },
      },
      didParseCell: function (data) {
        if (data.row.index === 0 && [1, 2].includes(data.column.index)) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: function (data) {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 && totalRows.includes(data.column.index)) ||
            (totalLines.includes(data.row.index) &&
              totalRows.includes(data.column.index)),
          totalDoubleLines.includes(data.row.index) &&
            totalRows.includes(data.column.index),
          24
        );
      },
      lineSettings: {
        totalLines,
        totalDoubleLines,
        totalRows,
        labelRows: totalRows,
      },
    });
  }

  if (!isExcel) pdf.startY = pdf.doc.lastAutoTable.finalY + 10;

  let assetContentColumns = [""];

  let assetTotalLines = [];
  let assetSubTotalLines = [];
  let assetTotalDoubleLines = [];
  let assetSubTotalDoubleLines = [];

  let assetContentData = [];
  let assetLabelRow = [""];

  if (assetsRows && assetsRows.length > 0) {
    if (forView)
      assetContentColumns.push(autoTextColor("Income\nProducing Asset?"));
    assetContentColumns.push("Total Asset");
    assetContentColumns.push("Non-income Producing Assets");

    if (forView) {
      assetContentColumns.push(redText("Reference"));
    }

    if (forView) assetLabelRow.push("");
    assetLabelRow.push("$", "$");
    if (forView) assetLabelRow.push("");
    assetContentData.push(assetLabelRow);
    assetContentData.push([boldAutoColumn("Assets at cost")]);

    assetsRows.map((row) => {
      let content = [row.description];

      if (forView)
        content.push(autoTextColor(row.isIncomeProducing ? "Yes" : "No"));

      content.push(formatAmount(row.amount || 0));

      content.push(
        !row.isIncomeProducing ? formatAmount(row.amount) : formatAmount(0)
      );

      if (forView) content.push(redText(row?.reference ?? ""));

      assetContentData.push(content);
    });
    if (forView)
      assetContentData.push([
        "",
        "",
        formatAmount(totalAssets || 0),
        formatAmount(totalNonIncomeAssets || 0),
      ]);
    else
      assetContentData.push([
        "",
        formatAmount(totalAssets),
        formatAmount(totalNonIncomeAssets),
      ]);
    assetSubTotalLines.push(assetContentData.length - 2);
    assetSubTotalLines.push(assetContentData.length - 1);
    assetTotalLines.push(assetContentData.length - 2);
    assetTotalLines.push(assetContentData.length - 1);
    assetTotalDoubleLines.push(assetContentData.length - 1);
    assetSubTotalDoubleLines.push(assetContentData.length - 1);
  }

  let needLinesArray = forView ? [2, 3] : [1, 2];

  if (assetContentData.length > 0)
    buildAutoTable({
      pdf,
      customStartY: pdf.startY,
      checkHeaderCondition: true,
      head: [assetContentColumns],
      body: assetContentData,
      schedule,
      company,
      columnStyles: forView
        ? {
            0: { halign: "left" },
            1: { halign: "center", cellWidth: 32 },
            2: { halign: "right", cellWidth: 25 },
            3: { halign: "right", cellWidth: 25 },
            4: { halign: "center" },
          }
        : {
            0: { halign: "left", minCellWidth: 30 },
            1: { halign: "right", cellWidth: 25 },
            2: { halign: "right", cellWidth: 25 },
            3: { halign: "center" },
          },
      didParseCell: function (data) {
        let needLinesArray = forView ? [2, 3] : [1, 2];
        if (
          data.row.index === 0 &&
          needLinesArray.includes(data.column.index)
        ) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: function (data) {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 &&
            needLinesArray.includes(data.column.index)) ||
            (assetTotalLines.includes(data.row.index) &&
              needLinesArray.includes(data.column.index)) ||
            (assetSubTotalLines.includes(data.row.index) &&
              needLinesArray.includes(data.column.index)),
          (assetTotalDoubleLines.includes(data.row.index) &&
            needLinesArray.includes(data.column.index)) ||
            (assetSubTotalDoubleLines.includes(data.row.index) &&
              needLinesArray.includes(data.column.index)),
          24
        );
      },
      lineSettings: {
        totalLines: assetTotalLines,
        subTotalLines: assetSubTotalLines,
        totalDoubleLines: assetTotalDoubleLines,
        subTotalDoubleLines: assetSubTotalDoubleLines,
        labelRows: needLinesArray,
        totalRows: needLinesArray,
        subTotalRows: needLinesArray,
      },
    });

  if (!isExcel) pdf.startY = pdf.doc.lastAutoTable.finalY + 12;

  let disallowableExpensesColumns = [
    {
      content: "Disallowable Interest Expense",
      colSpan: 6,
      styles: { halign: "left" },
    },
  ];

  let disallowableExpensesContent = [];

  if (!isExcel)
    disallowableExpensesContent.push([
      {
        content: formatAmount(totalNonIncomeAssets),
        styles: {
          fontStyle: "normal",
          lineWidth: {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0.1,
          },
          lineColor: [0, 0, 0],
          styles: {
            cellPadding: { top: 0, bottom: 0 },
            minCellHeight: 100,
            halign: "center",
          },
        },
      },
    ]);

  disallowableExpensesContent.push([
    isExcel
      ? `${formatAmount(totalNonIncomeAssets)}/${formatAmount(totalAssets)}`
      : formatAmount(totalAssets),
    {
      content: "X",
      styles: { cellPadding: { top: -2, bottom: 0 }, halign: "center" },
    },
    {
      content: formatAmount(totalInterestExpenses),
      styles: { cellPadding: { top: -2, bottom: 0 }, halign: "center" },
    },
    {
      content: "=",
      styles: { cellPadding: { top: -2, bottom: 0 }, halign: "center" },
    },
    {
      content: formatAmount(disAllowableInterest),
      styles: { cellPadding: { top: -2, bottom: 0 }, halign: "left" },
    },
  ]);

  disallowableExpensesContent.push([
    "",
    "",
    {
      content: "Added back / Main Comp",
      colSpan: 3,
      styles: { cellPadding: { top: -2, bottom: 0 }, halign: "right" },
    },
  ]);

  if (disallowableExpensesContent.length > 0) {
    buildAutoTable({
      pdf,
      customStartY: pdf.startY,
      checkHeaderCondition: true,
      head: [disallowableExpensesColumns],
      body: disallowableExpensesContent,
      schedule,
      company,
      columnStyles: {
        0: { halign: "center", cellWidth: 35 },
        1: { halign: "center", cellWidth: 20 },
        2: { halign: "center", cellWidth: 30 },
        3: { halign: "center", cellWidth: 18 },
        4: { halign: "center", cellWidth: 24 },
      },
      didParseCell: function (data) {},
      didDrawCell: function (data) {},
      lineSettings: {
        totalLines: [],
        totalDoubleLines: [],
        labelRows: [],
        totalRows: [],
        subTotalRows: [],
      },
    });
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    interestExpenseRows.concat(assetsRows),
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateMedicalExpensePdf = (
  forView,
  schedule,
  company,
  employeeRemunerationRows,
  medicalExpenseRows,
  totalEmployeeRemuneration,
  totalMedicalExpense,
  disallowableExpenses,
  expenseCappedAt,
  expenseCappedAmount,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);

  let contentColumns = ["", "", "", "Sch"];
  let totalLines = [];
  let subTotalLines = [];
  let totalDoubleLines = [];

  if (forView) {
    contentColumns.push(redText("Reference"));
  }

  let contentData = [];
  let labelRow = ["", "$", "$", ""];
  if (forView) labelRow.push("");
  contentData.push(labelRow);

  if (employeeRemunerationRows && employeeRemunerationRows.length > 0) {
    contentData.push([boldAutoColumn("Employee Remuneration")]);
    employeeRemunerationRows.map((row) => {
      let content = [
        row.description,
        "",
        formatAmount(row.amount),
        row.source?.sch_no ?? "",
      ];

      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }

      contentData.push(content);
    });

    contentData.push(["", "", formatAmount(totalEmployeeRemuneration)]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
  }

  if (medicalExpenseRows && medicalExpenseRows.length > 0) {
    contentData.push([]);
    contentData.push([boldAutoColumn("Medical Expenses")]);

    medicalExpenseRows.map((row) => {
      let content = [
        row.description,
        formatAmount(row.amount),
        "",
        row.source?.sch_no ?? "",
      ];

      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }

      contentData.push(content);
    });

    subTotalLines.push(contentData.length - 1);
    contentData.push([
      boldAutoColumn("Total Medical Expenses"),
      "",
      formatAmount(totalMedicalExpense),
    ]);

    contentData.push([]);
    contentData.push([
      boldAutoColumn(`Medical expense capped at ${expenseCappedAt}%`),
      "",
      formatAmount(-expenseCappedAmount),
    ]);

    contentData.push([]);
    contentData.push([
      boldAutoColumn("Disallowable Medical Expenses"),
      "",
      formatAmount(disallowableExpenses),
    ]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);

    if (disallowableExpenses !== 0)
      contentData.push([
        "",
        "",
        autoTextColor("Added back / Main Comp", [0, 0, 0]),
      ]);
  }

  if (contentData.length > 0) {
    const totalRows = [2];
    const subTotalRows = [1];
    const labelRows = [1, 2];

    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left", minCellWidth: 60 },
        1: { halign: "right", cellWidth: 25 },
        2: { halign: "right", cellWidth: 25 },
        3: { halign: "center", minCellWidth: 15 },
        4: { halign: "center" },
      },
      didParseCell: function (data) {
        if (data.row.index === 0 && [1, 2].includes(data.column.index)) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: function (data) {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 && [1, 2].includes(data.column.index)) ||
            (totalLines.includes(data.row.index) &&
              [2].includes(data.column.index)) ||
            (subTotalLines.includes(data.row.index) &&
              [1].includes(data.column.index)),
          totalDoubleLines.includes(data.row.index) &&
            [2].includes(data.column.index),
          24
        );
      },
      lineSettings: {
        totalLines,
        totalDoubleLines,
        subTotalLines,
        totalRows,
        subTotalRows,
        labelRows,
      },
    });
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    employeeRemunerationRows.concat(medicalExpenseRows),
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateMovementPdf = (
  forView,
  schedule,
  company,
  openingBalance,
  movementBalance,
  closingBalance,
  movementLines,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);

  let contentColumns = ["", "", "", "Sch"];
  let totalLines = [];
  let subTotalLines = [];
  let totalDoubleLines = [];
  let subTotalDoubleLines = [];

  if (forView) {
    contentColumns.push(redText("Reference"));
  }

  let contentData = [];
  let labelRow = ["", "$", "$", ""];
  if (forView) labelRow.push("");
  contentData.push(labelRow);

  if (movementLines && movementLines.length > 0) {
    contentData.push([]);
    contentData.push(["Opening Balance", formatAmount(openingBalance)]);
    contentData.push([]);

    movementLines.slice(1).map((line) => {
      let content = [
        line.name,
        formatAmount(line.amount),
        formatAmount(line.balance),
        line.source?.sch_no ?? "",
      ];

      if (forView) {
        content.push(redText(line?.reference ?? ""));
      }

      contentData.push(content);
    });

    contentData.push([
      "",
      formatAmount(movementBalance),
      formatAmount(closingBalance),
    ]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    subTotalLines.push(contentData.length - 2);
    subTotalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
    subTotalDoubleLines.push(contentData.length - 1);

    if (closingBalance !== 0)
      contentData.push([
        "",
        "",
        autoTextColor(
          `${closingBalance > 0 ? "Added back" : "Deduct"} / Main Comp`,
          [0, 0, 0]
        ),
      ]);
  }

  if (contentData.length > 0) {
    const totalRows = [1, 2];
    const subTotalRows = [1];
    const labelRows = [1, 2];

    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left", minCellWidth: 50 },
        1: { halign: "right", cellWidth: 25 },
        2: { halign: "right", cellWidth: 25 },
        3: { halign: "center" },
        4: { halign: "center" },
      },
      didParseCell: function (data) {
        if (data.row.index === 0 && labelRows.includes(data.column.index)) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: function (data) {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 && labelRows.includes(data.column.index)) ||
            (totalLines.includes(data.row.index) &&
              totalRows.includes(data.column.index)) ||
            (subTotalLines.includes(data.row.index) &&
              totalRows.includes(data.column.index)),
          (totalDoubleLines.includes(data.row.index) &&
            totalRows.includes(data.column.index)) ||
            (subTotalDoubleLines.includes(data.row.index) &&
              totalRows.includes(data.column.index)),
          24
        );
      },
      lineSettings: {
        totalLines,
        totalDoubleLines,
        totalRows,
        subTotalRows,
        labelRows,
      },
    });
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    movementLines,
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

const buildYearClaimTable = (
  schedule,
  company,
  pdf,
  tableRows,
  doc,
  startY,
  fs,
  {
    totalQualifyingCost,
    totalQualifyingCostBF,
    totalCostIncurred,
    totalDeduction,
    totalCostCF,
  },
  isSdg,
  { isFirstTable, isLastTable, totalAmount },
  isExcel = false
) => {
  if (tableRows && tableRows.length > 0) {
    let contentColumns = isSdg
      ? [
          "YA",
          "Start of 3 Year Claim",
          "Renovation Cost Incurred",
          "Qualifying R&R Cost",
          "Qualifying R&R Cost b/f",
          "No. of Years of Claim",
          "No. of Years b/f",
          "Current YA S14N Deduction",
          "Qualifying R&R Cost c/f",
          "No. of Years c/f",
        ]
      : [
          "YA",
          "Start of 3 Year Claim",
          "Renovation cost (func. $)",
          "Ex. rate",
          "Renovation cost (S$)",
          "Qualifying R&R cost (S$)",
          "Qualifying R&R cost (func. $)",
          "Qualifying R&R Cost b/f",
          "No. of Years of Claim",
          "No. of Years b/f",
          "Current YA S14N Deduction",
          "Qualifying R&R Cost c/f",
          "No. of Years c/f",
        ];

    let totalLines = [];
    let totalDoubleLines = [];

    let contentData = [];
    let labelRow = isSdg
      ? ["", "", "$", "$", "$", "", "", "$", "$", ""]
      : ["", "", "$", "", "$", "$", "$", "$", "", "", "$", "$", ""];
    contentData.push(labelRow);

    tableRows.map((row) => {
      let content = isSdg
        ? [
            row["YA"].value.toString(),
            row["start3Year"].value ? "Yes" : "No",
            formatAmount(row["costIncurred"].value.toString()),
            row["qualifyingCost"].value.toString(),
            row["qualifyingCostBF"].value.toString(),
            row["yearsClaim"].value.toString(),
            row["yearsBF"].value.toString(),
            row["deduction"].value.toString(),
            row["costCF"].value.toString(),
            row["yearsCF"].value.toString(),
          ]
        : [
            row["YA"].value.toString(),
            row["start3Year"].value ? "Yes" : "No",
            formatAmount(row["costIncurredFunc"].value.toString()),
            row["exRate"].value.toString(),
            formatAmount(row["costIncurred"].value.toString()),
            row["qualifyingCost"].value.toString(),
            row["qualifyingCostFunc"].value.toString(),
            row["qualifyingCostBF"].value.toString(),
            row["yearsClaim"].value.toString(),
            row["yearsBF"].value.toString(),
            row["deduction"].value.toString(),
            row["costCF"].value.toString(),
            row["yearsCF"].value.toString(),
          ];

      contentData.push(content);
    });

    contentData.push(
      isSdg
        ? [
            "",
            "",
            formatAmount(totalCostIncurred),
            formatAmount(totalQualifyingCost),
            formatAmount(totalQualifyingCostBF),
            "",
            "",
            formatAmount(totalDeduction),
            formatAmount(totalCostCF),
            "",
          ]
        : [
            "",
            "",
            "",
            "",
            formatAmount(totalCostIncurred),
            formatAmount(totalQualifyingCost),
            "",
            formatAmount(totalQualifyingCostBF),
            "",
            "",
            formatAmount(totalDeduction),
            formatAmount(totalCostCF),
            "",
          ]
    );
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);

    let additionalLines = [];
    if (isLastTable) {
      if (isFirstTable)
        contentData.push(
          isSdg
            ? [
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                autoTextColor("Deduct / Main Comp", [0, 0, 0]),
              ]
            : [
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                "",
                autoTextColor("Deduct / Main Comp", [0, 0, 0]),
              ]
        );

      if (!isFirstTable) {
        contentData.push([]);
        if (isSdg) {
          contentData.push([
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            formatAmount(totalAmount),
          ]);
          additionalLines.push(contentData.length - 1);

          contentData.push([
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            autoTextColor("Deduct / Main Comp", [0, 0, 0]),
          ]);
        } else {
          contentData.push([
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            formatAmount(totalAmount),
          ]);
          additionalLines.push(contentData.length - 1);

          contentData.push([
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            autoTextColor("Deduct / Main Comp", [0, 0, 0]),
          ]);
        }
      }
    }
    let labelNeedLines = isSdg ? [2, 3, 4, 7, 8] : [2, 4, 5, 6, 7, 10, 11];
    let rowNeedLines = isSdg ? [2, 3, 4, 7, 8] : [4, 5, 7, 10, 11];
    if (isExcel) contentData.push([]);
    if (contentData.length > 0)
      buildAutoTable({
        pdf,
        head: [contentColumns],
        body: contentData,
        customStartY: pdf.startY,
        checkHeaderCondition: true,
        schedule,
        company,
        columnStyles: isSdg
          ? {
              0: { halign: "center" },
              1: { halign: "center" },
              2: { halign: "right" },
              3: { halign: "right" },
              4: { halign: "right" },
              5: { halign: "center" },
              6: { halign: "center" },
              7: { halign: "right" },
              8: { halign: "right" },
              9: { halign: "center" },
            }
          : {
              0: { halign: "center" },
              1: { halign: "center" },
              2: { halign: "right" },
              3: { halign: "center" },
              4: { halign: "right" },
              5: { halign: "right" },
              6: { halign: "right" },
              7: { halign: "right" },
              8: { halign: "center" },
              9: { halign: "center" },
              10: { halign: "right" },
              11: { halign: "right" },
              12: { halign: "center" },
            },
        didParseCell: function (data) {
          if (
            data.row.index === 0 &&
            labelNeedLines.includes(data.column.index)
          ) {
            data.cell.styles.halign = "center";
          }
        },
        didDrawCell: function (data) {
          beautyAutoLine(
            data,
            doc,
            (data.row.index === 0 &&
              labelNeedLines.includes(data.column.index)) ||
              (totalLines.includes(data.row.index) &&
                rowNeedLines.includes(data.column.index)) ||
              (additionalLines.includes(data.row.index) &&
                [rowNeedLines[rowNeedLines.length - 2]].includes(
                  data.column.index
                )),
            (totalDoubleLines.includes(data.row.index) &&
              rowNeedLines.includes(data.column.index)) ||
              (additionalLines.includes(data.row.index) &&
                [rowNeedLines[rowNeedLines.length - 2]].includes(
                  data.column.index
                ))
          );
        },
        lineSettings: {
          totalLines,
          totalDoubleLines,
          totalRows: rowNeedLines,
          subTotalLines: additionalLines,
          subTotalDoubleLines: additionalLines,
          subTotalRows: [rowNeedLines[rowNeedLines.length - 2]],
          labelRows: labelNeedLines,
        },
      });

    return isExcel ? startY : doc.lastAutoTable.finalY + 12;
  }

  return startY;
};

export const generateRenovationPdf = (
  forView,
  schedule,
  company,
  rennovationRows,
  mode,
  isSdg,
  tableRows,
  secondTableRows,
  totalRennovations,
  totalQualifyingCost,
  totalQualifyingCostBF,
  totalCostIncurred,
  totalDeduction,
  totalCostCF,
  totalSecondCostIncurred,
  totalSecondQualifyingCost,
  totalSecondQualifyingCostBF,
  totalSecondDeduction,
  totalSecondCostCF,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(true, isExcel);

  if (!isSdg) {
    pdf.fs = 9;
  }

  if (rennovationRows && rennovationRows.length > 0) {
    let contentColumns = ["", "", "Sch"];
    let totalLines = [];
    let totalDoubleLines = [];

    if (forView) {
      contentColumns.push(redText("Reference"));
    }

    let contentData = [];
    let labelRow = ["", "$", ""];
    if (forView) labelRow.push("");
    contentData.push(labelRow);

    rennovationRows.map((row) => {
      let content = [
        row.description,
        formatAmount(row.amount),
        row.source?.sch_no ?? "",
      ];

      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }

      contentData.push(content);
    });

    contentData.push([
      boldAutoColumn("Current Year R&R Cost Incurred"),
      formatAmount(totalRennovations),
    ]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
    if (isExcel) contentData.push([]);

    if (contentData.length > 0) {
      const totalRows = [1];
      const labelRows = [1];
      buildAutoTable({
        pdf,
        checkHeaderCondition: true,
        head: [contentColumns],
        body: contentData,
        schedule,
        company,
        columnStyles: {
          0: { halign: "left", minCellWidth: 100 },
          1: { halign: "right", cellWidth: 25 },
          2: { halign: "center", minCellWidth: 15 },
          3: { halign: "center" },
        },
        didParseCell: function (data) {
          if (data.row.index === 0 && labelRows.includes(data.column.index)) {
            data.cell.styles.halign = "center";
          }
        },
        didDrawCell: function (data) {
          beautyAutoLine(
            data,
            pdf.doc,
            (data.row.index === 0 && labelRows.includes(data.column.index)) ||
              (totalLines.includes(data.row.index) &&
                totalRows.includes(data.column.index)),
            totalDoubleLines.includes(data.row.index) &&
              totalRows.includes(data.column.index),
            24
          );
        },
        lineSettings: {
          totalLines,
          totalDoubleLines,
          totalRows,
          labelRows,
        },
      });
    }
  }

  pdf.startY = buildYearClaimTable(
    schedule,
    company,
    pdf,
    tableRows,
    pdf.doc,
    pdf.startY,
    pdf.fs,
    {
      totalQualifyingCost: totalQualifyingCost,
      totalQualifyingCostBF: totalQualifyingCostBF,
      totalCostIncurred: totalCostIncurred,
      totalDeduction: totalDeduction,
      totalCostCF: totalCostCF,
    },
    isSdg,
    {
      isFirstTable: true,
      isLastTable: !(secondTableRows && secondTableRows.length > 0),
    },
    isExcel
  );
  if (
    mode === "2x3 years claim" &&
    secondTableRows &&
    secondTableRows.length > 0
  ) {
    pdf.startY = buildYearClaimTable(
      schedule,
      company,
      pdf,
      secondTableRows,
      pdf.doc,
      pdf.startY,
      pdf.fs,
      {
        totalQualifyingCost: totalSecondQualifyingCost,
        totalQualifyingCostBF: totalSecondQualifyingCostBF,
        totalCostIncurred: totalSecondCostIncurred,
        totalDeduction: totalSecondDeduction,
        totalCostCF: totalSecondCostCF,
      },
      isSdg,
      {
        isFirstTable: false,
        isLastTable: true,
        totalAmount: totalDeduction + totalSecondDeduction,
      },
      isExcel
    );
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    rennovationRows,
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateSavvyPdf = (
  forView,
  company,
  schedule,
  scheduleData,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);

  let contentColumns = ["", "", "Sch"];
  if (forView) {
    contentColumns.push(redText("Reference"));
  }

  let totalLines = [];
  let totalDoubleLines = [];

  let contentData = [["", "$"]];

  if (scheduleData && scheduleData.length > 0) {
    scheduleData.map((row) => {
      let content = [
        row.name,
        formatAmount(row.amount, false),
        row.source?.sch_no ?? "",
      ];

      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }

      contentData.push(content);
    });

    const totalAmount = scheduleData
      .reduce((sum, row) => sum + parseFloat(row.amount || 0), 0)
      .toFixed(2);
    contentData.push(["", formatAmount(totalAmount, false)]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);

    if (schedule.taxAdjustment && schedule.taxAdjustment !== "") {
      contentData.push([
        "",
        autoTextColor(`${schedule.taxAdjustment} / Main Comp`, [0, 0, 0]),
      ]);
    }
  }

  if (contentData.length > 0) {
    const totalRows = [1];
    const labelRows = [1];
    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left", minCellWidth: 50 },
        1: { halign: "right", cellWidth: 25 },
        2: { halign: "center", cellWidth: 25 },
        3: { halign: "center" },
        4: { halign: "center" },
      },
      didParseCell: function (data) {
        if (data.row.index === 0 && labelRows.includes(data.column.index)) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: function (data) {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 && labelRows.includes(data.column.index)) ||
            (totalLines.includes(data.row.index) &&
              totalRows.includes(data.column.index)),
          totalDoubleLines.includes(data.row.index) &&
            totalRows.includes(data.column.index),
          24
        );
      },
      lineSettings: {
        totalLines,
        totalDoubleLines,
        totalRows,
        labelRows,
      },
    });
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    scheduleData,
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateFullBreakdownPdf = (
  forView,
  schedule,
  company,
  sourceData,
  grossBalance,
  adjustedTotalBalance,
  totalSourceAmount,
  lines,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);

  let contentColumns = ["", "", "", "Sch"];
  let totalLines = [];
  let subTotalLines = [];
  let totalDoubleLines = [];
  let subTotalDoubleLines = [];

  if (forView) {
    contentColumns.push(autoTextColor("Internal\nReference"));
  }

  let contentData = [];
  let labelRow = ["", "$", "$", ""];
  if (forView) labelRow.push("");
  contentData.push(labelRow);

  if (lines && lines.length > 0) {
    contentData.push([]);
    lines.map((line) => {
      let content = [
        line.name,
        formatAmount(line.amount),
        formatAmount(line.balance),
        line.schedule && line.schedule.sch_id
          ? (line.schedule.adjustment && line.schedule.adjustment !== "none"
              ? `${line.schedule.adjustment} / `
              : "") + `Sch ${line.schedule.scheduleNo}`
          : "",
      ];

      if (forView) content.push(autoTextColor(line.reference));

      contentData.push(content);
    });

    contentData.push([
      "",
      formatAmount(grossBalance),
      formatAmount(adjustedTotalBalance),
    ]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    subTotalLines.push(contentData.length - 2);
    subTotalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
    subTotalDoubleLines.push(contentData.length - 1);

    const mismatchedRows = [];

    if (sourceData && sourceData.length > 0) {
      for (const row of sourceData) {
        const schNo = row?.source?.sch_no;
        if (
          isAutoload(row.status) &&
          schNo &&
          !mismatchedRows.includes(schNo)
        ) {
          mismatchedRows.push(schNo);
        }
      }
    }

    contentData.push([
      "",
      sourceData
        ? grossBalance === totalSourceAmount
          ? `${mismatchedRows.join("/")}`
          : "Not Matched"
        : "",
      `${adjustedTotalBalance >= 0 ? "Added back" : "Deduct"} / Main Comp`,
    ]);
  }

  const refColumnIndex = 4;
  if (forView && contentData.length > 0) {
    const { body: enhancedBody, referenceDetails } = enhanceReferenceColumn(
      contentData,
      refColumnIndex,
      15
    );
    contentData.length = 0;
    contentData.push(...enhancedBody);
    const refDetailsText = formatReferenceDetails(referenceDetails);
    if (refDetailsText) {
      if (reviewNotes !== "") reviewNotes += "\n\n";
      reviewNotes += "[Reference Details]\n" + refDetailsText;
    }
  }

  if (contentData.length > 0) {
    const totalRows = [1, 2];
    const subTotalRows = [1, 2];
    const labelRows = [1, 2];
    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left", minCellWidth: 50 },
        1: { halign: "right", cellWidth: 30 },
        2: { halign: "right", cellWidth: 30 },
        3: { halign: "center" },
        4: { halign: "center" },
      },
      didParseCell: (data) => {
        if (data.row.index === 0 && labelRows.includes(data.column.index)) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: (data) => {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 && labelRows.includes(data.column.index)) ||
            (totalLines.includes(data.row.index) &&
              totalRows.includes(data.column.index)) ||
            (subTotalLines.includes(data.row.index) &&
              subTotalRows.includes(data.column.index)),
          (totalDoubleLines.includes(data.row.index) &&
            [2].includes(data.column.index)) ||
            (subTotalDoubleLines.includes(data.row.index) &&
              subTotalRows.includes(data.column.index)),
          28
        );
      },
      lineSettings: {
        totalLines,
        totalDoubleLines,
        subTotalLines,
        totalRows,
        labelRows,
        subTotalRows,
      },
    });
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    lines,
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generatePartialBreakdownPdf = (
  forView,
  schedule,
  company,
  grossBalance,
  adjustedTotalBalance,
  lines,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);

  let contentColumns = ["", "", "", "Sch"];
  let totalLines = [];
  let subTotalLines = [];
  let totalDoubleLines = [];
  let subTotalDoubleLines = [];

  if (forView) {
    contentColumns.push(autoTextColor("Internal\nReference"));
  }

  let contentData = [];

  if (lines && lines.length > 0) {
    let labelRow = ["", "$", "$", ""];
    if (forView) labelRow.push("");
    contentData.push(labelRow);

    contentData.push(["The above includes the following:"]);
    contentData.push([]);
    lines.map((line) => {
      let content = [
        line.name,
        formatAmount(line.amount),
        formatAmount(line.balance),
        line.schedule && line.schedule.sch_id
          ? (line.schedule.adjustment && line.schedule.adjustment !== "none"
              ? `${line.schedule.adjustment} / `
              : "") + `Sch ${line.schedule.scheduleNo}`
          : "",
      ];

      if (forView) content.push(autoTextColor(line.reference));

      contentData.push(content);
    });

    contentData.push([
      "",
      formatAmount(grossBalance),
      formatAmount(adjustedTotalBalance),
    ]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    subTotalLines.push(contentData.length - 2);
    subTotalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
    subTotalDoubleLines.push(contentData.length - 1);

    if (adjustedTotalBalance !== 0)
      contentData.push([
        "",
        "",
        `${adjustedTotalBalance > 0 ? "Added back" : "Deduct"} / Main Comp`,
      ]);
  }

  const refColumnIndexPartial = 4;
  if (forView && contentData.length > 0) {
    const { body: enhancedBody, referenceDetails } = enhanceReferenceColumn(
      contentData,
      refColumnIndexPartial,
      15
    );
    contentData.length = 0;
    contentData.push(...enhancedBody);
    const refDetailsText = formatReferenceDetails(referenceDetails);
    if (refDetailsText) {
      if (reviewNotes !== "") reviewNotes += "\n\n";
      reviewNotes += "[Reference Details]\n" + refDetailsText;
    }
  }

  if (contentData.length > 0) {
    const totalRows = [1, 2];
    const subTotalRows = [1, 2];
    const labelRows = [1, 2];
    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right", cellWidth: 25 },
        2: { halign: "right", cellWidth: 25 },
        3: { halign: "center" },
        4: { halign: "center" },
      },
      didParseCell: (data) => {
        if (data.row.index === 0 && labelRows.includes(data.column.index)) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: (data) => {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 && labelRows.includes(data.column.index)) ||
            (totalLines.includes(data.row.index) &&
              totalRows.includes(data.column.index)) ||
            (subTotalLines.includes(data.row.index) &&
              subTotalRows.includes(data.column.index)),
          (totalDoubleLines.includes(data.row.index) &&
            [2].includes(data.column.index)) ||
            (subTotalDoubleLines.includes(data.row.index) &&
              [1, 2].includes(data.column.index)),
          24
        );
      },
      lineSettings: {
        totalLines,
        totalDoubleLines,
        subTotalLines,
        subTotalDoubleLines,
        totalRows,
        subTotalRows,
        labelRows,
      },
    });
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    lines,
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateRentalIncomePdf = (
  forView,
  schedule,
  company,
  address,
  hasVacantPeriod,
  vacantPeriods,
  rentalPeriods,
  grossIncomeRows,
  expensesRows,
  totalGrossIncome,
  netRentalIncome,
  foreignTaxSettings,
  notes,
  reviewNotes,
  shareOfExpenses,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);

  let rentalInfoColumns = [];
  let rentalInfoData = [];

  let hasAddress = false;
  if (address) {
    rentalInfoColumns.push("Address of property rented out:");
    rentalInfoData.push([address.address_line1]);
    rentalInfoData.push([address.address_line2]);
    rentalInfoData.push([address.address_line3]);
    rentalInfoData.push([]);
    hasAddress = true;
  }

  if (rentalInfoData.length > 0) {
    buildAutoTable({
      pdf,
      head: [rentalInfoColumns],
      body: rentalInfoData,
      schedule,
      company,
      columnStyles: { 0: { halign: "left", minCellWidth: 60 } },
      customHeadStyles: {
        fontStyle: "bold",
        halign: "left",
        textColor: [0, 0, 0],
      },
      lineSettings: {},
    });
  }

  const rentPeriodColumns = ["", "Start Date", "", "End Date", "No. of Days"];
  const rentPeriodData = [];

  // Handle vacant periods (array)
  if (Array.isArray(vacantPeriods) && vacantPeriods.length > 0) {
    vacantPeriods.forEach((period, index) => {
      rentPeriodData.push([
        boldAutoColumn(
          `Vacant period ${vacantPeriods.length > 1 ? `#${index + 1}` : ""}`
        ), // Label only once
        formatDateWithYear(period.startDate, "short"),
        "to",
        formatDateWithYear(period.endDate, "short"),
        formatAmount(period.days),
      ]);
    });

    if (vacantPeriods.length > 1) {
      const totalVacantDays = vacantPeriods.reduce(
        (sum, period) => sum + period.days,
        0
      );

      rentPeriodData.push([
        boldAutoColumn("Total Vacant Periods:"),
        "",
        "",
        "",
        formatAmount(totalVacantDays),
      ]);
    }
  } else if (
    vacantPeriods &&
    vacantPeriods.startDate &&
    vacantPeriods.endDate
  ) {
    rentPeriodData.push([
      boldAutoColumn("Vacant period"),
      formatDateWithYear(vacantPeriods.startDate, "short"),
      "to",
      formatDateWithYear(vacantPeriods.endDate, "short"),
      formatAmount(vacantPeriods.days),
    ]);
  }

  // Handle rental periods (array)
  if (Array.isArray(rentalPeriods) && rentalPeriods.length > 0) {
    rentalPeriods.forEach((period, index) => {
      rentPeriodData.push([
        index === 0
          ? boldAutoColumn(
              `Period of rental in basis period${
                rentalPeriods.length > 1 ? ` #${index + 1}` : ""
              }:`
            )
          : "",
        formatDateWithYear(period.startDate, "short"),
        "to",
        formatDateWithYear(period.endDate, "short"),
        formatAmount(period.days),
      ]);
    });
  } else if (
    rentalPeriods &&
    rentalPeriods.startDate &&
    rentalPeriods.endDate
  ) {
    rentPeriodData.push([
      boldAutoColumn("Period of rental in basis period:"),
      formatDateWithYear(rentalPeriods.startDate, "short"),
      "to",
      formatDateWithYear(rentalPeriods.endDate, "short"),
      formatAmount(rentalPeriods.days),
    ]);
  }

  let hasRentPeriod = false;
  if (rentPeriodData && rentPeriodData.length > 0) {
    hasRentPeriod = true;
    buildAutoTable({
      pdf,
      customStartY: hasAddress ? pdf.startY - 10 : 65,
      checkHeaderCondition: true,
      head: [rentPeriodColumns],
      body: rentPeriodData,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "center" },
        4: { halign: "center" },
        5: { halign: "center" },
      },
      didDrawCell: function (data) {
        beautyAutoLine(data, pdf.doc, [4].includes(data.column.index));
      },
      lineSettings: {},
    });
  }

  let contentColumns = [
    "",
    "",
    "Gross Expenses",
    "Non-Deductible Expenses",
    "",
    "Sch",
  ];

  if (forView) {
    contentColumns.push(redText("Reference"));
  }

  let contentData = [];
  let totalLines = [];
  let totalDoubleLines = [];

  let labelRow = ["", "", "$", "$", "$", ""];
  if (forView) labelRow.push("");
  contentData.push(labelRow);
  contentData.push([boldAutoColumn("Gross Rental Income")]);

  if (grossIncomeRows && grossIncomeRows.length > 0) {
    grossIncomeRows.map((row) => {
      let content = [
        row.description,
        "",
        "",
        "",
        formatAmount(row.amount),
        row.source?.sch_no,
        forView && redText(row?.reference ?? ""),
      ];

      contentData.push(content);
    });

    contentData.push([
      !expensesRows || expensesRows.length <= 0
        ? !(company.companyType === "investment" && shareOfExpenses)
          ? boldAutoColumn("Net Rental Income")
          : ""
        : "",
      "",
      "",
      "",
      formatAmount(totalGrossIncome),
      "",
    ]);

    totalLines.push(contentData.length - 2);
    if (!expensesRows || expensesRows.length <= 0) {
      if (company.companyType === "investment" && shareOfExpenses) {
        contentData.push([]);
        contentData.push([
          boldAutoColumn("Less: Share of expenses "),
          "",
          "",
          "",
          formatAmount(shareOfExpenses?.amount || 0, true, true),
          shareOfExpenses?.source ?? "",
        ]);
        contentData.push([]);

        contentData.push([
          boldAutoColumn("Net Rental Income"),
          "",
          "",
          "",
          formatAmount(netRentalIncome),
        ]);
        totalLines.push(contentData.length - 2);
      }

      totalLines.push(contentData.length - 1);
      totalDoubleLines.push(contentData.length - 1);
    }
  }

  if (expensesRows && expensesRows.length > 0) {
    contentData.push([
      boldAutoColumn("Less: Rental expenses"),
      boldAutoColumn("Tax Treatment"),
    ]);

    expensesRows.map((row) => {
      let nonDeductibleAmount = expensesAdjustment(
        row.adjustment,
        row.amount,
        hasVacantPeriod,
        vacantPeriods,
        rentalPeriods
      );
      let adjustedAmount = row.amount - nonDeductibleAmount;

      let content = [
        row.description,
        row.adjustment,
        formatAmount(-row.amount),
        formatAmount(nonDeductibleAmount),
        formatAmount(-adjustedAmount),
        row.source?.sch_no,
        forView && redText(row?.reference ?? ""),
      ];

      contentData.push(content);
    });

    if (company.companyType === "investment" && shareOfExpenses) {
      contentData.push([]);
      contentData.push([
        boldAutoColumn("Less: Share of expenses "),
        "",
        "",
        "",
        formatAmount(shareOfExpenses?.amount || 0, true, true),
        shareOfExpenses?.source ?? "",
      ]);
      contentData.push([]);
    }

    contentData.push([
      boldAutoColumn("Net Rental Income"),
      "",
      "",
      "",
      formatAmount(netRentalIncome),
    ]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
  }

  if (schedule.taxAdjustment === "Added Back")
    contentData.push([
      "",
      "",
      "",
      "",
      autoTextColor("Added back / Main Comp", [0, 0, 0]),
    ]);

  if (schedule.taxAdjustment === "Unremitted Foreign Income") {
    contentData.push([
      "",
      "",
      "",
      "",
      autoTextColor(foreignTaxSettings?.unremittedSchNo || "", [0, 0, 0]),
    ]);
  }

  if (
    foreignTaxSettings.foreignTaxSuffered &&
    foreignTaxSettings.foreignTaxSuffered > 0 &&
    (schedule.taxAdjustment === "Unremitted Foreign Income" ||
      schedule.taxAdjustment === "Added Back")
  ) {
    contentData.push([]);

    contentData.push([
      boldAutoColumn("Foreign Tax Suffered"),
      "",
      "",
      "",
      formatAmount(foreignTaxSettings.foreignTaxSuffered, false),
    ]);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
    if (foreignTaxSettings.isTagToFTC) {
      contentData.push([
        "",
        "",
        "",
        "",
        lineBreakText(foreignTaxSettings.ftcSchNo || "", { top: 3, bottom: 0 }),
      ]);
    }
  }

  if (contentData.length > 0) {
    const totalRows = [4];
    const labelRows = [2, 3, 4];
    buildAutoTable({
      pdf,
      customStartY: hasAddress || hasRentPeriod ? pdf.startY : 65,
      head: [contentColumns],
      body: contentData,
      checkHeaderCondition: true,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left", minCellWidth: 50 },
        1: { halign: "center" },
        2: { halign: "right", cellWidth: 24 },
        3: { halign: "right", cellWidth: 30 },
        4: { halign: "right", cellWidth: 24 },
        5: { halign: "center", cellWidth: 15 },
        6: { halign: "center" },
      },
      didParseCell: function (data) {
        if (data.row.index === 0 && labelRows.includes(data.column.index)) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: function (data) {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 && labelRows.includes(data.column.index)) ||
            (totalLines.includes(data.row.index) &&
              totalRows.includes(data.column.index)),
          totalDoubleLines.includes(data.row.index) &&
            totalRows.includes(data.column.index)
        );
      },
      lineSettings: {
        totalLines,
        totalDoubleLines,
        totalRows,
        labelRows,
      },
    });
  }

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotes,
    grossIncomeRows.concat(expensesRows),
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};

export const generateAllocationExpensesPdf = (
  forView,
  schedule,
  company,
  incomeRows,
  statutoryExpensesRows,
  commonExpensesRows,
  cappedRate,
  expenseCappedAmount,
  cappedVsCommon,
  finalAllowedExpenses,
  deductibleAllocationRows,
  totalDeductibleExpenses,
  notes,
  reviewNotes,
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);

  let contentColumns = ["", "", "", "Sch"];

  if (forView) {
    contentColumns.push(redText("Reference"));
  }

  let contentData = [];
  let labelRow = ["", "$", "$", ""];
  let totalLines = [];
  let subTotalLines = [];
  let subTotalDoubleLines = [];
  let totalDoubleLines = [];

  contentData.push(labelRow);

  if (incomeRows && incomeRows.length > 0) {
    contentData.push([boldAutoColumn("Gross Investment Income")]);
    incomeRows.map((row) => {
      let content = [
        row.description,
        "",
        formatAmount(row.amount),
        row.source?.sch_no ?? "",
      ];

      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }
      contentData.push(content);
    });

    const totalIncome = incomeRows.reduce((sum, row) => sum + row.amount, 0);
    contentData.push(["", "", formatAmount(totalIncome), "[A]"]);

    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
  }

  if (statutoryExpensesRows && statutoryExpensesRows.length > 0) {
    contentData.push([boldAutoColumn("Statutory Expenses")]);
    statutoryExpensesRows.map((row) => {
      let content = [row.description];
      content.push("", formatAmount(row.amount));
      content.push(row.source?.sch_no ?? "");
      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }
      contentData.push(content);
    });

    let totalStatutoryExpenses = statutoryExpensesRows.reduce(
      (sum, row) => sum + row.amount,
      0
    );
    contentData.push([
      boldAutoColumn("Total Statutory Expenses"),
      "",
      formatAmount(totalStatutoryExpenses),
    ]);
    if (isExcel) totalLines.push(contentData.length - 3);
    totalLines.push(contentData.length - 2);
    contentData.push([]);
  }

  if (commonExpensesRows && commonExpensesRows.length > 0) {
    contentData.push([boldAutoColumn("Common Expenses")]);
    commonExpensesRows.map((row) => {
      let content = [row.description];
      content.push(formatAmount(row.amount), "");
      content.push(row.source?.sch_no ?? "");
      if (forView) {
        content.push(redText(row?.reference ?? ""));
      }
      contentData.push(content);
    });

    let totalCommonExpenses = commonExpensesRows.reduce(
      (sum, row) => sum + row.amount,
      0
    );
    contentData.push([
      boldAutoColumn("Total Common Expenses"),
      formatAmount(totalCommonExpenses),
      "",
    ]);
    subTotalLines.push(contentData.length - 1);
    subTotalLines.push(contentData.length - 2);
    subTotalDoubleLines.push(contentData.length - 1);
    contentData.push([]);

    contentData.push([
      boldAutoColumn(`Expense cap (${cappedRate}% of [A])`),
      formatAmount(expenseCappedAmount),
      formatAmount(cappedVsCommon.deductionAmount),
    ]);
    subTotalLines.push(contentData.length - 1);
  }

  contentData.push([]);
  contentData.push([
    boldAutoColumn("Total deductible expenses"),
    "",
    formatAmount(finalAllowedExpenses),
  ]);
  if (isExcel) totalLines.push(contentData.length - 3);
  totalLines.push(contentData.length - 2);
  totalLines.push(contentData.length - 1);
  totalDoubleLines.push(contentData.length - 1);

  if (deductibleAllocationRows && deductibleAllocationRows.length > 0) {
    contentData.push([]);
    contentData.push([
      boldAutoColumn("Share of common and statutory expenses"),
    ]);

    deductibleAllocationRows.map((row) => {
      let content = [row.description];
      content.push("", formatAmount(row.deductibleAmount));
      content.push(row.taggedTo ?? "");

      if (row.original_amount && row.original_amount < 0) {
        content.push(redText("(Negative Income)"));
      }

      contentData.push(content);
    });
    contentData.push(["", "", formatAmount(totalDeductibleExpenses)]);
    totalLines.push(contentData.length - 2);
    totalLines.push(contentData.length - 1);
    totalDoubleLines.push(contentData.length - 1);
  }

  if (contentData.length > 0)
    buildAutoTable({
      pdf,
      head: [contentColumns],
      body: contentData,
      schedule,
      company,
      columnStyles: {
        0: { halign: "left", minCellWidth: 50 },
        1: { halign: "right", cellWidth: 25 },
        2: { halign: "right", cellWidth: 25 },
        3: { halign: "center" },
        4: { halign: "center" },
      },
      didParseCell: function (data) {
        if (data.row.index === 0 && [1, 2].includes(data.column.index)) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawCell: function (data) {
        beautyAutoLine(
          data,
          pdf.doc,
          (data.row.index === 0 && [1, 2].includes(data.column.index)) ||
            (subTotalLines.includes(data.row.index) &&
              [1].includes(data.column.index)) ||
            (totalLines.includes(data.row.index) &&
              [2].includes(data.column.index)),
          (totalDoubleLines.includes(data.row.index) &&
            [2].includes(data.column.index)) ||
            (subTotalDoubleLines.includes(data.row.index) &&
              [1].includes(data.column.index)),
          24
        );
      },
      lineSettings: {
        totalLines,
        totalDoubleLines,
        subTotalLines,
        subTotalDoubleLines,
        totalRows: [2],
        subTotalRows: [1],
      },
    });

  const allocationWorkingReviewNotes =
    forView && !isExcel && deductibleAllocationRows?.length > 0
      ? [
          "Allocation expense workings:",
          ...deductibleAllocationRows.map((row) => {
            const working = row.working || {};
            const incomeAmount = working.incomeAmount ?? row.incomeAmount ?? 0;
            const totalIncome = working.totalIncome ?? 0;
            const statutoryExpenses = working.statutoryExpenses ?? 0;
            const commonExpensesUsed = working.commonExpensesUsed ?? 0;
            const expensePoolUsed =
              working.expensePoolUsed ?? statutoryExpenses + commonExpensesUsed;
            const roundedAmount =
              working.roundedDeductibleAmount ?? row.deductibleAmount ?? 0;
            const treatment = working.commonExpenseTreatment
              ? ` (${working.commonExpenseTreatment})`
              : "";

            return `${row.description || "Untitled item"}${
              treatment
            }: ${formatAmount(roundedAmount)} = ${formatAmount(
              incomeAmount
            )} / ${formatAmount(totalIncome)} * ${formatAmount(
              expensePoolUsed
            )}`;
          }),
        ].join("\n")
      : "";

  const reviewNotesWithAllocationWorkings = [
    reviewNotes,
    allocationWorkingReviewNotes,
  ]
    .filter(Boolean)
    .join("\n\n");

  buildAutoNote(
    pdf,
    forView,
    company,
    schedule,
    notes,
    reviewNotesWithAllocationWorkings,
    [],
    footerSettings
  );

  return pdf.getDoc(forView, company, schedule);
};
